import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert, Button, Card, Col, Collapse, Divider, Form, Input, InputNumber, Modal, Popconfirm, Progress, Radio, Row, Segmented, Select,
    Space, Statistic, Switch, Table, Tabs, Tag, Tooltip, Typography, message,
} from 'antd';
import {
    CheckCircleOutlined, DeleteOutlined, EditOutlined, KeyOutlined, LockOutlined, PlayCircleOutlined,
    PlusOutlined, ReloadOutlined, RollbackOutlined, SafetyCertificateOutlined, SettingOutlined,
} from '@ant-design/icons';
import apiClient from '../../../shared/services/apiClient';
import PageHeader from '../../../shared/components/PageHeader';
import { useTranslation } from '../../../shared/i18n';
import { formatBytes } from '../../../shared/utils/format';
import { PLATFORM_CURRENCY } from '../../../shared/utils/money';

// Admin: scheduled encrypted VM backups (restic). Admin API only:
// /api/evm/admin/backups/* (controller\evm\admin\BackupAdminController).

const { Text } = Typography;
const BASE = '/api/evm/admin/backups';
const call = async (method, url, data) => {
    try {
        const res = method === 'get' ? await apiClient.get(BASE + url, { params: data }) : await apiClient.post(BASE + url, data || {});
        return res.data || {};
    } catch (e) {
        return { status: 'error', msg: e.response?.data?.msg || e.message };
    }
};
const okRes = (r) => r?.status === 'success';
const act = async (t, method, url, data, fail) => {
    const r = await call(method, url, data);
    if (okRes(r)) message.success(r.msg || t('Submitted'));
    else message.error(r.msg || fail || t('Operation failed'));
    return okRes(r) ? r : null;
};
const GiB = 1024 * 1024 * 1024;
const bytes = (v) => (v === null || v === undefined ? '-' : formatBytes(Number(v)));
const dur = (s) => {
    s = Number(s) || 0;
    if (s <= 0) return '-';
    const h = Math.floor(s / 3600); const m = Math.floor((s % 3600) / 60);
    return h ? `${h}h ${m}m` : m ? `${m}m ${s % 60}s` : `${s}s`;
};
const ACTIVE = ['queued', 'running', 'cancelling'];
const STATUS_COLOR = { queued: 'default', running: 'processing', cancelling: 'warning', success: 'success', failed: 'error', cancelled: 'default' };
const STATUS_TEXT = { queued: '排队中', running: '运行中', cancelling: '取消中', success: '成功', failed: '失败', cancelled: '已取消' };
const KIND_TEXT = { backup: '备份', restore: '恢复', restore_test: '恢复演练', forget: '删除', check: '校验', key_rotate: '密钥轮换', target_test: '目标测试', prune: '清理' };
const CONS = { application: ['应用一致', 'green'], crash: ['崩溃一致', 'orange'], offline: ['关机', 'blue'], quiesced: ['一致快照', 'green'] };
const SOURCE_TEXT = { plan: '套餐', addon: '附加服务', admin: '管理员', all: '全局', admin_off: '已关闭' };
const TYPE_COLOR = { s3: 'blue', sftp: 'purple', local: 'default' };

const StatusTag = ({ s }) => {
    const { t } = useTranslation();
    return <Tag color={STATUS_COLOR[s] || 'default'}>{t(STATUS_TEXT[s] || s || '-')}</Tag>;
};
const ConsTag = ({ c, note }) => {
    const { t } = useTranslation();
    if (!c) return '-';
    const [label, color] = CONS[c] || [c, 'default'];
    const tag = <Tag color={color}>{t(label)}</Tag>;
    return note ? <Tooltip title={note}>{tag}</Tooltip> : tag;
};
const ResTag = ({ r }) => {
    const { t } = useTranslation();
    return r === 'container' ? <Tag color="cyan" style={{ marginInlineEnd: 0 }}>{t('Container')}</Tag> : <Tag style={{ marginInlineEnd: 0 }}>VM</Tag>;
};
const nameOf = (list, id) => {
    const x = (list || []).find((r) => Number(r.id) === Number(id));
    return x ? x.name : (id ? `#${id}` : '-');
};
const hoursAgo = (ts) => (ts ? (Date.now() - new Date(String(ts).replace(' ', 'T')).getTime()) / 3600000 : Infinity);

/** Loads a GET endpoint into state. */
const useLoad = (url, params, pick = (d) => d) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const key = JSON.stringify(params || {});
    const pickRef = useRef(pick);
    pickRef.current = pick;
    const load = useCallback(async (silent) => {
        if (!silent) setLoading(true);
        const r = await call('get', url, JSON.parse(key));
        if (!silent) setLoading(false);
        if (okRes(r)) setData(pickRef.current(r.data));
        else if (!silent) message.error(r.msg || 'error');
    }, [url, key]);
    useEffect(() => { load(); }, [load]);
    return [data, load, loading];
};

// ---------------------------------------------------------------- overview

const OverviewTab = () => {
    const { t } = useTranslation();
    const [d, load, loading] = useLoad('/overview', { days: 7 });
    const rep = d?.report || {};
    const kinds = useMemo(() => {
        const m = {};
        (rep.jobs || []).forEach((j) => {
            m[j.kind] = m[j.kind] || { kind: j.kind, success: 0, failed: 0, other: 0, added: 0 };
            const n = Number(j.n) || 0;
            if (j.status === 'success') m[j.kind].success += n;
            else if (j.status === 'failed') m[j.kind].failed += n;
            else m[j.kind].other += n;
            m[j.kind].added += Number(j.added) || 0;
        });
        return Object.values(m);
    }, [rep.jobs]);
    return (
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
            {d && !d.master_key?.configured && <Alert type="error" showIcon message={t('Master key not configured')} />}
            <Row gutter={[12, 12]}>
                <Col xs={12} md={6}><Card size="small"><Statistic title={t('Backup points')} value={rep.points ?? '-'} /></Card></Col>
                <Col xs={12} md={6}><Card size="small"><Statistic title={t('Protected instances')} value={rep.vms_protected ?? '-'} /></Card></Col>
                <Col xs={12} md={6}><Card size="small"><Statistic title={t('Storage used')} value={bytes(rep.stored_bytes)} /></Card></Col>
                <Col xs={12} md={6}><Card size="small"><Statistic title={t('Dedup ratio')} value={rep.dedup_ratio ? `${rep.dedup_ratio}x` : '-'} /></Card></Col>
            </Row>
            <Card size="small" title={t('Jobs, last {{n}} days', { n: rep.days || 7 })} extra={<Button size="small" type="text" icon={<ReloadOutlined />} onClick={() => load()} loading={loading} />}>
                <Table size="small" rowKey="kind" pagination={false} dataSource={kinds} columns={[
                    { title: t('Type'), dataIndex: 'kind', render: (k) => t(KIND_TEXT[k] || k) },
                    { title: t('Success'), dataIndex: 'success', render: (v) => <Text type="success">{v}</Text> },
                    { title: t('Failed'), dataIndex: 'failed', render: (v) => (v ? <Text type="danger">{v}</Text> : 0) },
                    { title: t('Other'), dataIndex: 'other' },
                    { title: t('Data added'), dataIndex: 'added', render: bytes },
                ]} />
            </Card>
            <Card size="small" title={t('Open alerts')}>
                <Table size="small" rowKey={(r, i) => `${r.type}-${r.target_id}-${i}`} pagination={false} dataSource={rep.open_alerts || []} columns={[
                    { title: t('Level'), dataIndex: 'level', render: (v) => <Tag color={v === 'critical' ? 'red' : v === 'warning' ? 'orange' : 'default'}>{v}</Tag> },
                    { title: t('Type'), dataIndex: 'type' },
                    { title: t('Object'), render: (_, r) => `${r.target_type || ''} #${r.target_id || '-'}` },
                    { title: t('Content'), dataIndex: 'message' },
                    { title: t('Last seen'), dataIndex: 'last_seen_at' },
                ]} />
            </Card>
        </Space>
    );
};

// ---------------------------------------------------------------- targets

const TargetModal = ({ value, onClose, onSaved, nodes, areas, zones }) => {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const [saving, setSaving] = useState(false);
    const type = Form.useWatch('type', form);
    const scope = Form.useWatch('scope_type', form);
    const editing = !!value?.id;
    useEffect(() => {
        if (!value) return;
        form.resetFields();
        form.setFieldsValue({
            name: '', type: 's3', enabled: true, priority: 100, quota_gb: 0, scope_type: 'global', scope_ids: [],
            ...value,
            config: { provider: 'aws', prefix: 'evm-backup', bucket_lookup: 'auto', port: 22, ...(value.config || {}) },
            secret: {},
        });
    }, [value, form]);
    const save = async () => {
        const v = await form.validateFields();
        const secret = Object.fromEntries(Object.entries(v.secret || {}).filter(([, x]) => x && String(x).trim() !== ''));
        const body = { ...v, scope_ids: v.scope_type === 'global' ? [] : (v.scope_ids || []), config: v.config || {} };
        if (Object.keys(secret).length) body.secret = secret; else delete body.secret;
        setSaving(true);
        const r = await call('post', editing ? `/targets/${value.id}` : '/targets', body);
        setSaving(false);
        if (okRes(r)) { message.success(t('Saved')); onSaved(); } else message.error(r.msg || t('Failed to save'));
    };
    const keep = editing && value?.has_secret ? t('Set; leave empty to keep') : undefined;
    const scopeOpts = scope === 'node' ? nodes.map((n) => ({ value: n.id, label: n.name }))
        : scope === 'zone' ? zones.map((z) => ({ value: z.id, label: z.name }))
            : areas.map((a) => ({ value: a.id, label: a.name }));
    return (
        <Modal open={!!value} title={editing ? t('Edit target') : t('Add target')} onCancel={onClose} onOk={save} confirmLoading={saving} destroyOnClose width={560}>
            <Form form={form} layout="vertical">
                <Space wrap>
                    <Form.Item name="name" label={t('Name')} rules={[{ required: true }]}><Input style={{ width: 220 }} /></Form.Item>
                    <Form.Item name="type" label={t('Type')}>
                        <Segmented disabled={editing} options={[{ value: 's3', label: 'S3' }, { value: 'sftp', label: 'SFTP' }, { value: 'local', label: t('Local') }]} />
                    </Form.Item>
                </Space>
                {type === 's3' && (
                    <>
                        <Space wrap>
                            <Form.Item name={['config', 'provider']} label={t('Provider')}>
                                <Select style={{ width: 140 }} options={[['aws', 'AWS'], ['r2', 'Cloudflare R2'], ['b2', 'Backblaze B2'], ['minio', 'MinIO'], ['other', t('Other')]].map(([k, l]) => ({ value: k, label: l }))} />
                            </Form.Item>
                            <Form.Item name={['config', 'endpoint']} label="Endpoint" rules={[{ required: true }]}><Input style={{ width: 300 }} placeholder="https://s3.example.com" /></Form.Item>
                        </Space>
                        <Space wrap>
                            <Form.Item name={['config', 'bucket']} label="Bucket" rules={[{ required: true }]}><Input style={{ width: 200 }} /></Form.Item>
                            <Form.Item name={['config', 'prefix']} label={t('Prefix')}><Input style={{ width: 140 }} /></Form.Item>
                            <Form.Item name={['config', 'region']} label="Region"><Input style={{ width: 120 }} /></Form.Item>
                        </Space>
                        <Space wrap>
                            <Form.Item name={['secret', 'access_key']} label="Access Key" tooltip={value?.access_key_hint || undefined}><Input.Password style={{ width: 230 }} placeholder={keep} autoComplete="new-password" /></Form.Item>
                            <Form.Item name={['secret', 'secret_key']} label="Secret Key"><Input.Password style={{ width: 230 }} placeholder={keep} autoComplete="new-password" /></Form.Item>
                        </Space>
                    </>
                )}
                {type === 'sftp' && (
                    <>
                        <Space wrap>
                            <Form.Item name={['config', 'host']} label={t('Host')} rules={[{ required: true }]}><Input style={{ width: 220 }} /></Form.Item>
                            <Form.Item name={['config', 'port']} label={t('Port')}><InputNumber min={1} max={65535} /></Form.Item>
                            <Form.Item name={['config', 'user']} label={t('Username')} rules={[{ required: true }]}><Input style={{ width: 140 }} /></Form.Item>
                        </Space>
                        <Form.Item name={['config', 'path']} label={t('Path')} tooltip={t('Relative to the SFTP root')} rules={[{ required: true }]}><Input placeholder="backups/evm" /></Form.Item>
                        <Form.Item name={['secret', 'private_key']} label={t('SSH private key')} tooltip={t('OpenSSH private key, without passphrase')}>
                            <Input.TextArea rows={3} placeholder={keep} style={{ fontFamily: 'monospace' }} />
                        </Form.Item>
                    </>
                )}
                {type === 'local' && (
                    <Form.Item name={['config', 'path']} label={t('Path')} rules={[{ required: true }]} extra={t('This node only; unusable for restore if the node fails')}>
                        <Input placeholder="/data/evm-backup" />
                    </Form.Item>
                )}
                <Space wrap align="start">
                    <Form.Item name="scope_type" label={t('Scope')}>
                        <Select style={{ width: 120 }} options={[{ value: 'global', label: t('Global') }, { value: 'area', label: t('Region') }, { value: 'zone', label: t('Availability zone') }, { value: 'node', label: t('Node') }]} />
                    </Form.Item>
                    {scope && scope !== 'global' && (
                        <Form.Item name="scope_ids" label=" " rules={[{ required: true, message: t('Select a scope') }]}>
                            <Select mode="multiple" style={{ minWidth: 260 }} options={scopeOpts} optionFilterProp="label" />
                        </Form.Item>
                    )}
                </Space>
                <Space wrap>
                    <Form.Item name="enabled" label={t('Enable')} valuePropName="checked"><Switch /></Form.Item>
                    <Form.Item name="priority" label={t('Priority')} tooltip={t('Lower number wins')}><InputNumber min={0} max={10000} /></Form.Item>
                    <Form.Item name="quota_gb" label={t('Capacity limit')} tooltip={t('0 means unlimited')}><InputNumber min={0} addonAfter="GB" style={{ width: 140 }} /></Form.Item>
                </Space>
                {type !== 'local' && (
                    <Collapse ghost size="small" items={[{
                        key: 'adv', label: t('Advanced'), children: type === 's3' ? (
                            <Form.Item name={['config', 'bucket_lookup']} label={t('Bucket addressing')}>
                                <Segmented options={[{ value: 'auto', label: t('Automatic') }, { value: 'path', label: 'Path' }, { value: 'dns', label: 'DNS' }]} />
                            </Form.Item>
                        ) : (
                            <Form.Item name={['config', 'known_hosts']} label="known_hosts" tooltip={t('Empty = pinned on first successful test')}>
                                <Input.TextArea rows={2} style={{ fontFamily: 'monospace' }} />
                            </Form.Item>
                        ),
                    }]} />
                )}
            </Form>
        </Modal>
    );
};

const TargetsTab = ({ nodes, areas, zones }) => {
    const { t } = useTranslation();
    const [rows, load, loading] = useLoad('/targets', null, (d) => (Array.isArray(d) ? d : []));
    const [edit, setEdit] = useState(null);
    const [testing, setTesting] = useState({});
    const timers = useRef({});
    useEffect(() => () => Object.values(timers.current).forEach(clearInterval), []);
    const test = async (tg, nodeId) => {
        const r = await act(t, 'post', `/targets/${tg.id}/test`, nodeId ? { node_id: nodeId } : {});
        if (!r) return;
        const jobId = r.data?.job_id;
        setTesting((s) => ({ ...s, [tg.id]: true }));
        const stop = () => { clearInterval(timers.current[tg.id]); delete timers.current[tg.id]; setTesting((s) => ({ ...s, [tg.id]: false })); load(true); };
        let n = 0;
        timers.current[tg.id] = setInterval(async () => {
            n += 1;
            const j = await call('get', '/jobs', { kind: 'target_test', page_size: 10 });
            const job = (j.data?.items || []).find((x) => Number(x.id) === Number(jobId));
            if (!job || !ACTIVE.includes(job.status) || n > 100) stop();
        }, 3000);
    };
    const del = async (tg) => { if (await act(t, 'post', `/targets/${tg.id}/delete`)) load(); };
    const scopeText = (r) => {
        if (r.scope_type === 'global') return t('Global');
        const list = r.scope_type === 'node' ? nodes : r.scope_type === 'zone' ? zones : areas;
        return `${t({ area: '地区', zone: '可用区', node: '节点' }[r.scope_type] || r.scope_type)}: ${(r.scope_ids || []).map((id) => nameOf(list, id)).join(', ')}`;
    };
    const columns = [
        { title: t('Name'), dataIndex: 'name', render: (n, r) => <Space size={4}><Text>{n}</Text>{!r.enabled && <Tag>{t('Disable')}</Tag>}</Space> },
        { title: t('Type'), dataIndex: 'type', render: (v) => <Tag color={TYPE_COLOR[v]}>{v}</Tag> },
        { title: t('Location'), render: (_, r) => <Text type="secondary" style={{ fontSize: 12 }}>{r.type === 's3' ? `${r.config?.endpoint || ''}/${r.config?.bucket || ''}/${r.config?.prefix || ''}` : r.type === 'sftp' ? `${r.config?.user}@${r.config?.host}:${r.config?.path}` : r.config?.path}</Text> },
        { title: t('Scope'), render: (_, r) => scopeText(r) },
        { title: t('Priority'), dataIndex: 'priority' },
        {
            title: t('Usage'), render: (_, r) => (r.quota_gb > 0
                ? <Tooltip title={`${bytes(r.used_bytes)} / ${r.quota_gb} GB`}><Progress size="small" style={{ width: 120, margin: 0 }} percent={Math.min(100, Math.round((r.used_bytes / (r.quota_gb * GiB)) * 100))} /></Tooltip>
                : bytes(r.used_bytes)),
        },
        { title: t('Repos'), dataIndex: 'repos' },
        {
            title: t('Connectivity'), render: (_, r) => {
                if (testing[r.id]) return <Tag color="processing">{t('Testing')}</Tag>;
                if (r.secret_error) return <Tooltip title={r.secret_error}><Tag color="red">{t('Key error')}</Tag></Tooltip>;
                if (r.test_ok === null || r.test_ok === undefined) return <Tag>{t('Not tested')}</Tag>;
                return <Tooltip title={`${r.test_msg || ''} ${r.test_at || ''}`}><Tag color={r.test_ok ? 'green' : 'red'}>{r.test_ok ? t('Normal') : t('Failed')}</Tag></Tooltip>;
            },
        },
        {
            title: '', key: 'actions', render: (_, r) => (
                <Space size={4}>
                    <Button size="small" loading={!!testing[r.id]} onClick={() => test(r)}>{t('Test')}</Button>
                    <Button size="small" icon={<EditOutlined />} onClick={() => setEdit(r)} />
                    <Popconfirm title={t('Delete this target?')} okButtonProps={{ danger: true }} onConfirm={() => del(r)}>
                        <Button size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                </Space>
            ),
        },
    ];
    return (
        <>
            <Space style={{ marginBottom: 8 }}>
                <Button type="primary" icon={<PlusOutlined />} onClick={() => setEdit({})}>{t('Add target')}</Button>
                <Button icon={<ReloadOutlined />} onClick={() => load()} loading={loading} />
            </Space>
            <Table size="small" rowKey="id" loading={loading && !rows} dataSource={rows || []} columns={columns} pagination={false} scroll={{ x: 'max-content' }} />
            <TargetModal value={edit} nodes={nodes} areas={areas} zones={zones} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />
        </>
    );
};

// ---------------------------------------------------------------- policies

const POLICY_DEFAULT = {
    name: '', enabled: true, apply_all: false, template_ids: [], product_ids: [], ctr_apply_all: false, ctr_product_ids: [], interval_hours: 24,
    keep_last: 1, keep_daily: 7, keep_weekly: 4, keep_monthly: 0, keep_manual: 3, manual_per_day: 2, user_restore: true,
    window_start: null, window_end: null, consistency: 'auto', target_id: null,
};

const PolicyModal = ({ value, onClose, onSaved, templates, products, ctrProducts, targets }) => {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const [saving, setSaving] = useState(false);
    useEffect(() => { if (value) { form.resetFields(); form.setFieldsValue({ ...POLICY_DEFAULT, ...value }); } }, [value, form]);
    const save = async () => {
        const v = await form.validateFields();
        setSaving(true);
        const r = await call('post', value.id ? `/policies/${value.id}` : '/policies', v);
        setSaving(false);
        if (okRes(r)) { message.success(t('Saved')); onSaved(); } else message.error(r.msg || t('Failed to save'));
    };
    const num = (name, label, max = 1000, tip) => <Form.Item name={name} label={t(label)} tooltip={tip}><InputNumber min={0} max={max} style={{ width: 90 }} /></Form.Item>;
    return (
        <Modal open={!!value} title={value?.id ? t('Edit policy') : t('Add policy')} onCancel={onClose} onOk={save} confirmLoading={saving} destroyOnClose width={600}>
            <Form form={form} layout="vertical">
                <Space wrap>
                    <Form.Item name="name" label={t('Name')} rules={[{ required: true }]}><Input style={{ width: 240 }} /></Form.Item>
                    <Form.Item name="enabled" label={t('Enable')} valuePropName="checked"><Switch /></Form.Item>
                    <Form.Item name="apply_all" label={t('Default policy')} valuePropName="checked" tooltip={t('Instances whose plan has no policy also use this one')}><Switch /></Form.Item>
                </Space>
                <Form.Item name="template_ids" label={t('Plan')}>
                    <Select mode="multiple" optionFilterProp="label" options={(templates || []).map((x) => ({ value: x.id, label: x.name }))} />
                </Form.Item>
                <Space wrap align="start">
                    <Form.Item name="ctr_product_ids" label={t('Container plans')}>
                        <Select mode="multiple" optionFilterProp="label" style={{ minWidth: 300 }} options={(ctrProducts || []).map((x) => ({ value: x.id, label: x.name }))} />
                    </Form.Item>
                    <Form.Item name="ctr_apply_all" label={t('All containers')} valuePropName="checked" tooltip={t('Containers without a plan-bound policy also use this policy')}><Switch /></Form.Item>
                </Space>
                <Space wrap>
                    <Form.Item name="interval_hours" label={t('Interval')}><InputNumber min={1} max={744} addonAfter={t('hours')} style={{ width: 140 }} /></Form.Item>
                    {num('manual_per_day', '每日手动次数', 50)}
                    <Form.Item name="user_restore" label={t('User self-restore')} valuePropName="checked"><Switch /></Form.Item>
                </Space>
                <Divider orientation="left" plain style={{ margin: '4px 0 8px' }}>{t('Keep')}</Divider>
                <Space wrap>
                    {num('keep_daily', '天')}
                    {num('keep_weekly', '周')}
                    {num('keep_monthly', '月')}
                    {num('keep_last', '最近', 1000, t('Also keep the latest N'))}
                    {num('keep_manual', '手动', 100)}
                </Space>
                <Collapse ghost size="small" items={[{
                    key: 'adv', label: t('Advanced'), children: (
                        <>
                            <Form.Item name="product_ids" label={t('Product')}>
                                <Select mode="multiple" optionFilterProp="label" options={(products || []).map((x) => ({ value: x.id, label: x.name }))} />
                            </Form.Item>
                            <Space wrap>
                                <Form.Item name="window_start" label={t('Window start')} tooltip={t('Empty = global setting')}><InputNumber min={0} max={23} addonAfter={t('h')} style={{ width: 110 }} /></Form.Item>
                                <Form.Item name="window_end" label={t('Window end')}><InputNumber min={0} max={23} addonAfter={t('h')} style={{ width: 110 }} /></Form.Item>
                                <Form.Item name="consistency" label={t('Consistency')}>
                                    <Segmented options={[{ value: 'auto', label: t('Automatic') }, { value: 'crash', label: t('Crash-consistent') }]} />
                                </Form.Item>
                            </Space>
                            <Form.Item name="target_id" label={t('Storage target')}>
                                <Select allowClear placeholder={t('Auto (by scope)')} options={(targets || []).map((x) => ({ value: x.id, label: `${x.name} (${x.type})` }))} />
                            </Form.Item>
                        </>
                    ),
                }]} />
            </Form>
        </Modal>
    );
};

const PoliciesTab = () => {
    const { t } = useTranslation();
    const [d, load, loading] = useLoad('/policies');
    const [targets] = useLoad('/targets', null, (x) => (Array.isArray(x) ? x : []));
    const [edit, setEdit] = useState(null);
    const del = async (p) => { if (await act(t, 'post', `/policies/${p.id}/delete`)) load(); };
    const columns = [
        { title: t('Name'), dataIndex: 'name', render: (n, r) => <Space size={4}><Text>{n}</Text>{!r.enabled && <Tag>{t('Disable')}</Tag>}{r.apply_all && <Tag color="blue">{t('Default')}</Tag>}</Space> },
        {
            title: t('Plan'), render: (_, r) => (
                <>
                    {(r.template_ids || []).map((id) => <Tag key={id}>{nameOf(d?.templates, id)}</Tag>)}
                    {(r.ctr_product_ids || []).map((id) => <Tag key={`c${id}`} color="cyan">{nameOf(d?.ctr_products, id)}</Tag>)}
                    {r.ctr_apply_all && <Tag color="cyan">{t('All containers')}</Tag>}
                </>
            ),
        },
        { title: t('Interval'), dataIndex: 'interval_hours', render: (v) => t('Every {{n}} h', { n: v }) },
        { title: t('Keep'), render: (_, r) => `${r.keep_daily}${t('Days')} / ${r.keep_weekly}${t('weeks')} / ${r.keep_monthly}${t('Month')}` },
        { title: t('Manual'), render: (_, r) => `${r.manual_per_day}/${t('Days')} · ${t('Keep')} ${r.keep_manual}` },
        { title: t('Self-restore'), dataIndex: 'user_restore', render: (v) => (v ? <CheckCircleOutlined style={{ color: '#52c41a' }} /> : '-') },
        { title: t('Storage target'), dataIndex: 'target_id', render: (v) => (v ? nameOf(targets, v) : <Text type="secondary">{t('Automatic')}</Text>) },
        {
            title: '', key: 'actions', render: (_, r) => (
                <Space size={4}>
                    <Button size="small" icon={<EditOutlined />} onClick={() => setEdit(r)} />
                    <Popconfirm title={t('Delete this policy?')} okButtonProps={{ danger: true }} onConfirm={() => del(r)}>
                        <Button size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                </Space>
            ),
        },
    ];
    return (
        <>
            <Space style={{ marginBottom: 8 }}>
                <Button type="primary" icon={<PlusOutlined />} onClick={() => setEdit({})}>{t('Add policy')}</Button>
                <Button icon={<ReloadOutlined />} onClick={() => load()} loading={loading} />
            </Space>
            <Table size="small" rowKey="id" loading={loading && !d} dataSource={d?.items || []} columns={columns} pagination={false} scroll={{ x: 'max-content' }} />
            <PolicyModal value={edit} templates={d?.templates} products={d?.products} ctrProducts={d?.ctr_products} targets={targets} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />
        </>
    );
};

// ---------------------------------------------------------------- jobs

const JobsTab = ({ nodes }) => {
    const { t } = useTranslation();
    const [f, setF] = useState({ status: '', kind: '', vm_id: '', page: 1, page_size: 50 });
    const [d, load, loading] = useLoad('/jobs', f);
    const items = d?.items || [];
    const anyActive = items.some((j) => ACTIVE.includes(j.status));
    useEffect(() => {
        if (!anyActive) return undefined;
        const iv = setInterval(() => load(true), 5000);
        return () => clearInterval(iv);
    }, [anyActive, load]);
    const cancel = async (j) => { if (await act(t, 'post', `/jobs/${j.id}/cancel`)) load(true); };
    const retry = async (j) => { if (await act(t, 'post', `/jobs/${j.id}/retry`)) load(true); };
    const columns = [
        { title: 'ID', dataIndex: 'id' },
        { title: t('Type'), render: (_, j) => <Space size={2}>{t(KIND_TEXT[j.kind] || j.kind)}{j.trigger && <Text type="secondary" style={{ fontSize: 12 }}>· {j.trigger}</Text>}</Space> },
        { title: t('Instances'), dataIndex: 'vm_id', render: (v, j) => (v ? <Space size={4}><ResTag r={j.res_type} />#{v}{j.uid && <Text type="secondary" style={{ fontSize: 12 }}>u{j.uid}</Text>}</Space> : '-') },
        { title: t('Node'), dataIndex: 'node_id', render: (v) => nameOf(nodes, v) },
        {
            title: t('Status'), render: (_, j) => (j.status === 'running'
                ? <Tooltip title={j.progress_msg}><Progress size="small" style={{ width: 120, margin: 0 }} percent={Math.round(Number(j.progress) || 0)} status="active" /></Tooltip>
                : j.error ? <Tooltip title={j.error}><span><StatusTag s={j.status} /></span></Tooltip> : <StatusTag s={j.status} />),
        },
        { title: t('Consistency'), render: (_, j) => <ConsTag c={j.consistency} note={j.freeze_note} /> },
        { title: t('Added / total'), render: (_, j) => (j.bytes_total ? `${bytes(j.bytes_added)} / ${bytes(j.bytes_total)}` : '-') },
        { title: t('Time taken'), dataIndex: 'duration_sec', render: dur },
        { title: t('Created at'), dataIndex: 'created_at' },
        {
            title: '', key: 'actions', render: (_, j) => (
                <Space size={4}>
                    {['queued', 'running'].includes(j.status) && <Popconfirm title={t('Cancel this job?')} onConfirm={() => cancel(j)}><Button size="small">{t('Cancel')}</Button></Popconfirm>}
                    {['failed', 'cancelled'].includes(j.status) && j.kind === 'backup' && <Button size="small" onClick={() => retry(j)}>{t('Retry')}</Button>}
                </Space>
            ),
        },
    ];
    const set = (patch) => setF((x) => ({ ...x, page: 1, ...patch }));
    return (
        <>
            <Space wrap style={{ marginBottom: 8 }}>
                <Select style={{ width: 130 }} value={f.status} onChange={(v) => set({ status: v })} options={[{ value: '', label: t('All statuses') }, ...Object.keys(STATUS_TEXT).map((k) => ({ value: k, label: t(STATUS_TEXT[k]) }))]} />
                <Select style={{ width: 130 }} value={f.kind} onChange={(v) => set({ kind: v })} options={[{ value: '', label: t('All types') }, ...Object.keys(KIND_TEXT).map((k) => ({ value: k, label: t(KIND_TEXT[k]) }))]} />
                <Input.Search allowClear style={{ width: 160 }} placeholder={t('Instance ID')} onSearch={(v) => set({ vm_id: v.trim() })} />
                <Button icon={<ReloadOutlined />} onClick={() => load()} loading={loading} />
            </Space>
            <Table size="small" rowKey="id" loading={loading && !d} dataSource={items} columns={columns} scroll={{ x: 'max-content' }}
                pagination={{ current: f.page, pageSize: f.page_size, total: d?.total || 0, showSizeChanger: false, onChange: (p) => setF((x) => ({ ...x, page: p })) }} />
        </>
    );
};

// ---------------------------------------------------------------- points

const RestoreModal = ({ point, nodes, onClose, onDone }) => {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const [saving, setSaving] = useState(false);
    const mode = Form.useWatch('mode', form);
    const isCtr = point?.res_type === 'container';
    useEffect(() => { if (point) { form.resetFields(); form.setFieldsValue({ mode: 'same', power_on: 'keep', public_ip_num: 1, dev: point.disks?.[0]?.dev }); } }, [point, form]);
    const submit = async () => {
        const v = await form.validateFields();
        const body = { mode: isCtr ? 'same' : v.mode };
        if (body.mode === 'disk') body.dev = v.dev;
        if (body.mode !== 'new_vm') body.power_on = v.power_on;
        else Object.assign(body, { node_id: v.node_id, ip_pool_id: v.ip_pool_id || undefined, public_ip_num: v.public_ip_num ?? 1 });
        const go = async () => {
            setSaving(true);
            const r = await call('post', `/points/${point.id}/restore`, body);
            setSaving(false);
            if (!okRes(r)) { message.error(r.msg || t('Restore failed')); return; }
            if (r.data?.vm) message.success(t('Instance {{name}} created', { name: r.data.vm.hostname || r.data.vm.name || `#${r.data.vm.id}` }));
            else message.success(r.msg || t('Restore task submitted'));
            onDone();
        };
        if (body.mode === 'new_vm') go();
        else Modal.confirm({ title: isCtr ? t('This will overwrite the current container data') : t('This will overwrite the current disk data'), okButtonProps: { danger: true }, okText: t('Restore'), cancelText: t('Cancel'), onOk: go });
    };
    return (
        <Modal open={!!point} title={`${t('Restore')} · #${point?.id || ''} ${point?.vm_name || ''}`} onCancel={onClose} onOk={submit} confirmLoading={saving} okText={t('Restore')} destroyOnClose>
            <Form form={form} layout="vertical">
                {!isCtr && (
                    <Form.Item name="mode">
                        <Radio.Group optionType="button" options={[{ value: 'same', label: t('Whole VM') }, { value: 'disk', label: t('Single disk') }, { value: 'new_vm', label: t('New instance') }]} />
                    </Form.Item>
                )}
                {!isCtr && mode === 'disk' && (
                    <Form.Item name="dev" label={t('Disk')} rules={[{ required: true }]}>
                        <Select options={(point?.disks || []).map((x) => ({ value: x.dev, label: `${x.dev} · ${bytes(x.bytes)}` }))} />
                    </Form.Item>
                )}
                {!isCtr && mode === 'new_vm' && (
                    <>
                        <Form.Item name="node_id" label={t('Node')} rules={[{ required: true }]}>
                            <Select showSearch optionFilterProp="label" options={nodes.map((n) => ({ value: n.id, label: n.name }))} />
                        </Form.Item>
                        <Space wrap>
                            <Form.Item name="public_ip_num" label={t('Public IPs')}><InputNumber min={0} max={16} /></Form.Item>
                            <Form.Item name="ip_pool_id" label={t('IP pool ID')} tooltip={t('Empty = auto')}><InputNumber min={1} /></Form.Item>
                        </Space>
                    </>
                )}
                {(isCtr || mode !== 'new_vm') && (
                    <Collapse ghost size="small" items={[{
                        key: 'adv', label: t('Advanced'), children: (
                            <Form.Item name="power_on" label={t('Power after restore')}>
                                <Segmented options={[{ value: 'keep', label: t('Keep') }, { value: 'on', label: t('Power on') }, { value: 'off', label: t('Shut down') }]} />
                            </Form.Item>
                        ),
                    }]} />
                )}
            </Form>
        </Modal>
    );
};

const PointsTab = ({ nodes }) => {
    const { t } = useTranslation();
    const [f, setF] = useState({ vm_id: '', uid: '', status: 'available', page: 1 });
    const [d, load, loading] = useLoad('/points', f);
    const [restore, setRestore] = useState(null);
    const verify = async (p) => { await act(t, 'post', `/points/${p.id}/verify`); };
    const del = async (p) => { if (await act(t, 'post', `/points/${p.id}/delete`)) load(true); };
    const columns = [
        { title: 'ID', dataIndex: 'id' },
        { title: t('Instances'), render: (_, p) => <Space size={4}><ResTag r={p.res_type} /><Text>{p.vm_name || '-'}</Text><Text type="secondary" style={{ fontSize: 12 }}>#{p.vm_id} · u{p.uid}</Text></Space> },
        { title: t('Time'), dataIndex: 'created_at' },
        { title: t('Type'), dataIndex: 'kind', render: (v) => <Tag color={v === 'manual' ? 'purple' : 'default'}>{v === 'manual' ? t('Manual') : t('Scheduled')}</Tag> },
        { title: t('Consistency'), dataIndex: 'consistency', render: (v) => <ConsTag c={v} /> },
        { title: t('Size'), render: (_, p) => <Tooltip title={(p.disks || []).map((x) => `${x.dev}: ${bytes(x.bytes)}`).join(' / ')}>{bytes(p.total_bytes)}</Tooltip> },
        { title: t('Added'), render: (_, p) => bytes(p.added_packed || p.added_bytes) },
        { title: t('Node'), dataIndex: 'node_id', render: (v) => nameOf(nodes, v) },
        {
            title: t('Dry run'), render: (_, p) => (p.verify_ok === null || p.verify_ok === undefined ? '-'
                : <Tooltip title={`${p.verify_msg || ''} ${p.verified_at || ''}`}><Tag color={p.verify_ok ? 'green' : 'red'}>{p.verify_ok ? t('Allow') : t('Failed')}</Tag></Tooltip>),
        },
        { title: t('Status'), dataIndex: 'status', render: (v) => (v === 'available' ? <Tag color="green">{t('Available')}</Tag> : <Tag>{v}</Tag>) },
        {
            title: '', key: 'actions', render: (_, p) => p.status === 'available' && (
                <Space size={4}>
                    <Button size="small" icon={<RollbackOutlined />} onClick={() => setRestore(p)}>{t('Restore')}</Button>
                    <Tooltip title={t('Checks it can be restored; writes nothing')}><Button size="small" icon={<SafetyCertificateOutlined />} onClick={() => verify(p)}>{t('Dry run')}</Button></Tooltip>
                    <Popconfirm title={t('Delete this backup point?')} okButtonProps={{ danger: true }} onConfirm={() => del(p)}>
                        <Button size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                </Space>
            ),
        },
    ];
    const set = (patch) => setF((x) => ({ ...x, page: 1, ...patch }));
    return (
        <>
            <Space wrap style={{ marginBottom: 8 }}>
                <Input.Search allowClear style={{ width: 150 }} placeholder={t('Instance ID')} onSearch={(v) => set({ vm_id: v.trim() })} />
                <Input.Search allowClear style={{ width: 150 }} placeholder={t('User ID')} onSearch={(v) => set({ uid: v.trim() })} />
                <Segmented value={f.status} onChange={(v) => set({ status: v })} options={[{ value: 'available', label: t('Available') }, { value: 'all', label: t('All') }]} />
                <Button icon={<ReloadOutlined />} onClick={() => load()} loading={loading} />
            </Space>
            <Table size="small" rowKey="id" loading={loading && !d} dataSource={d?.items || []} columns={columns} scroll={{ x: 'max-content' }}
                pagination={{ current: f.page, pageSize: 50, total: d?.total || 0, showSizeChanger: false, onChange: (p) => setF((x) => ({ ...x, page: p })) }} />
            <RestoreModal point={restore} nodes={nodes} onClose={() => setRestore(null)} onDone={() => { setRestore(null); load(true); }} />
        </>
    );
};

// ---------------------------------------------------------------- vms

const VmModal = ({ vm, type, policies, targets, onClose, onSaved }) => {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const [saving, setSaving] = useState(false);
    useEffect(() => {
        if (!vm) return;
        const o = vm.override || {};
        form.setFieldsValue({ enabled: o.enabled === true ? 'on' : o.enabled === false ? 'off' : 'follow', policy_id: o.policy_id || null, target_id: o.target_id || null, points: o.points || null });
    }, [vm, form]);
    const save = async () => {
        const v = await form.validateFields();
        setSaving(true);
        const r = await call('post', `/vms/${vm.id}`, { type, enabled: v.enabled === 'on' ? true : v.enabled === 'off' ? false : null, policy_id: v.policy_id || null, target_id: v.target_id || null, points: v.points || null });
        setSaving(false);
        if (okRes(r)) { message.success(t('Saved')); onSaved(); } else message.error(r.msg || t('Failed to save'));
    };
    return (
        <Modal open={!!vm} title={`${t('Backup settings')} · ${vm?.hostname || vm?.name || ''}`} onCancel={onClose} onOk={save} confirmLoading={saving} destroyOnClose>
            <Form form={form} layout="vertical">
                <Form.Item name="enabled">
                    <Segmented options={[{ value: 'follow', label: t('Follow plan') }, { value: 'on', label: t('Force on') }, { value: 'off', label: t('Close') }]} />
                </Form.Item>
                <Form.Item name="policy_id" label={t('Policy')}>
                    <Select allowClear placeholder={t('Follow plan')} options={(policies || []).map((p) => ({ value: p.id, label: p.name }))} />
                </Form.Item>
                <Form.Item name="target_id" label={t('Storage target')}>
                    <Select allowClear placeholder={t('Auto (by scope)')} options={(targets || []).map((x) => ({ value: x.id, label: `${x.name} (${x.type})` }))} />
                </Form.Item>
                <Form.Item name="points" label={t('Restore points')} tooltip={vm?.quota ? t('Included {{a}} + extra {{b}}', { a: vm.quota.included, b: vm.quota.extra }) : undefined}>
                    <InputNumber min={0} max={365} placeholder={t('Follow plan')} style={{ width: 160 }} />
                </Form.Item>
            </Form>
        </Modal>
    );
};

const VmsTab = ({ nodes }) => {
    const { t } = useTranslation();
    const [f, setF] = useState({ q: '', page: 1, type: 'vm' });
    const [d, load, loading] = useLoad('/vms', f);
    const [pol] = useLoad('/policies', null, (x) => x?.items || []);
    const [targets] = useLoad('/targets', null, (x) => (Array.isArray(x) ? x : []));
    const [edit, setEdit] = useState(null);
    const run = async (v) => { await act(t, 'post', `/vms/${v.id}/run`, { type: f.type }); };
    const columns = [
        { title: t('Instances'), render: (_, v) => <Space size={4}><Text>{v.hostname || v.name}</Text><Text type="secondary" style={{ fontSize: 12 }}>#{v.id} · u{v.uid}</Text></Space> },
        { title: t('Node'), dataIndex: 'node_id', render: (x) => nameOf(nodes, x) },
        { title: t('Policy'), render: (_, v) => (v.policy ? <Space size={4}><Text>{v.policy}</Text>{v.source && <Tag>{t(SOURCE_TEXT[v.source] || v.source)}</Tag>}</Space> : <Tag>{t(SOURCE_TEXT[v.source] || '未开通')}</Tag>) },
        {
            title: t('Last success'), dataIndex: 'last_success_at', render: (x, v) => {
                if (!v.policy) return x || '-';
                const h = hoursAgo(x);
                const color = h > 72 ? '#ff4d4f' : h > 36 ? '#fa8c16' : undefined;
                return <Text style={{ color }}>{x || t('Never')}</Text>;
            },
        },
        { title: t('Failures in a row'), dataIndex: 'fail_count', render: (x) => (x ? <Text type="danger">{x}</Text> : 0) },
        {
            title: t('Restore points'), key: 'quota', render: (_, v) => (v.quota ? (
                <Tooltip title={t('Included {{a}} + extra {{b}}', { a: v.quota.included, b: v.quota.extra })}>
                    <Text>{v.quota.used} / {v.quota.total}</Text>{v.addon && <Tag style={{ marginLeft: 6 }}>{t('Add-on')}</Tag>}
                </Tooltip>
            ) : v.points),
        },
        {
            title: '', key: 'actions', render: (_, v) => (
                <Space size={4}>
                    <Popconfirm title={t('Back up now?')} onConfirm={() => run(v)}><Button size="small" icon={<PlayCircleOutlined />}>{t('Back up now')}</Button></Popconfirm>
                    <Button size="small" icon={<SettingOutlined />} onClick={() => setEdit(v)} />
                </Space>
            ),
        },
    ];
    return (
        <>
            <Space wrap style={{ marginBottom: 8 }}>
                <Segmented value={f.type} onChange={(type) => setF((x) => ({ ...x, type, page: 1 }))} options={[{ value: 'vm', label: t('Cloud servers') }, { value: 'container', label: t('Dedicated Containers') }]} />
                <Input.Search allowClear style={{ width: 220 }} placeholder={t('Name / ID / user ID')} onSearch={(q) => setF((x) => ({ ...x, q: q.trim(), page: 1 }))} />
                <Button icon={<ReloadOutlined />} onClick={() => load()} loading={loading} />
            </Space>
            <Table size="small" rowKey="id" loading={loading && !d} dataSource={d?.items || []} columns={columns} scroll={{ x: 'max-content' }}
                pagination={{ current: f.page, pageSize: 50, total: d?.total || 0, showSizeChanger: false, onChange: (p) => setF((x) => ({ ...x, page: p })) }} />
            <VmModal vm={edit} type={f.type} policies={pol} targets={targets} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(true); }} />
        </>
    );
};

// ---------------------------------------------------------------- nodes

const NodeModal = ({ node, onClose, onSaved }) => {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const [saving, setSaving] = useState(false);
    useEffect(() => {
        if (!node) return;
        const o = node.override || {};
        form.setFieldsValue({ enabled: o.enabled ?? true, max_jobs: o.max_jobs ?? null, upload_mbps: o.upload_mbps ?? null, read_mbps: o.read_mbps ?? null });
    }, [node, form]);
    const save = async () => {
        const v = await form.validateFields();
        setSaving(true);
        const r = await call('post', `/nodes/${node.id}`, v);
        setSaving(false);
        if (okRes(r)) { message.success(t('Saved')); onSaved(); } else message.error(r.msg || t('Failed to save'));
    };
    const ph = t('Global');
    return (
        <Modal open={!!node} title={`${t('Node backup')} · ${node?.name || ''}`} onCancel={onClose} onOk={save} confirmLoading={saving} destroyOnClose>
            <Form form={form} layout="vertical">
                <Form.Item name="enabled" label={t('Allow backups')} valuePropName="checked"><Switch /></Form.Item>
                <Space wrap>
                    <Form.Item name="max_jobs" label={t('Concurrent jobs')}><InputNumber min={1} max={8} placeholder={ph} style={{ width: 110 }} /></Form.Item>
                    <Form.Item name="upload_mbps" label={t('Upload limit')} tooltip={t('0 means unlimited')}><InputNumber min={0} max={10000} placeholder={ph} addonAfter="Mbps" style={{ width: 170 }} /></Form.Item>
                    <Form.Item name="read_mbps" label={t('Disk read limit')} tooltip={t('0 means unlimited')}><InputNumber min={0} max={10000} placeholder={ph} addonAfter="MB/s" style={{ width: 170 }} /></Form.Item>
                </Space>
            </Form>
        </Modal>
    );
};

const NodesTab = ({ nodes, reload, loading }) => {
    const { t } = useTranslation();
    const [edit, setEdit] = useState(null);
    const capTag = (label, v, required) => (v
        ? <Tooltip key={label} title={typeof v === 'string' ? v : undefined}><Tag color="green">{label}{typeof v === 'string' && label === 'restic' ? ` ${v}` : ''}</Tag></Tooltip>
        : <Tag key={label} color={required ? 'red' : 'default'}>{label} ✕</Tag>);
    const columns = [
        { title: t('Node'), dataIndex: 'name', render: (n, r) => <Space size={4}><Text>{n}</Text><Text type="secondary">#{r.id}</Text></Space> },
        { title: t('Status'), render: (_, r) => (r.effective?.enabled === false ? <Tag>{t('Disabled')}</Tag> : <Tag color={r.status === 'online' || r.status === 1 || r.status === 'active' ? 'green' : 'default'}>{String(r.status ?? '-')}</Tag>) },
        { title: 'Agent', dataIndex: 'agent_version', render: (v) => v || '-' },
        {
            title: t('Capabilities'), render: (_, r) => {
                const c = r.caps || {};
                if (!r.caps_at) return <Text type="secondary">{t('Not reported')}</Text>;
                return <Space size={2} wrap>{capTag('restic', c.restic, true)}{capTag('nbdcopy', c.nbdcopy, true)}{capTag('qemu-nbd', c.qemu_nbd)}{capTag('rbd', c.rbd)}</Space>;
            },
        },
        { title: t('Running / queued'), render: (_, r) => <Space size={4}><Tag color={r.running ? 'processing' : 'default'}>{r.running}</Tag><Tag>{r.queued}</Tag></Space> },
        {
            title: t('Limits'), render: (_, r) => {
                const e = r.effective || {};
                return <Text style={{ fontSize: 12 }} type={r.override ? undefined : 'secondary'}>{e.max_jobs} · {e.upload_mbps || '∞'} Mbps · {e.read_mbps || '∞'} MB/s</Text>;
            },
        },
        { title: '', key: 'actions', render: (_, r) => <Button size="small" icon={<SettingOutlined />} onClick={() => setEdit(r)} /> },
    ];
    return (
        <>
            <Space style={{ marginBottom: 8 }}><Button icon={<ReloadOutlined />} onClick={reload} loading={loading} /></Space>
            <Table size="small" rowKey="id" loading={loading && !nodes.length} dataSource={nodes} columns={columns} pagination={false} scroll={{ x: 'max-content' }} />
            <NodeModal node={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); reload(); }} />
        </>
    );
};

// ---------------------------------------------------------------- repos / keys

const ReposTab = () => {
    const { t } = useTranslation();
    const [d, load, loading] = useLoad('/repos');
    const [targets] = useLoad('/targets', null, (x) => (Array.isArray(x) ? x : []));
    const mk = d?.master_key || {};
    const rotate = async (r) => { if (await act(t, 'post', `/repos/${r.id}/rotate`)) load(true); };
    const columns = [
        { title: 'ID', dataIndex: 'id' },
        { title: t('User'), dataIndex: 'uid', render: (v) => `u${v}` },
        { title: t('Targets'), dataIndex: 'target_id', render: (v) => nameOf(targets, v) },
        { title: t('Path'), dataIndex: 'path', render: (v) => <Text code style={{ fontSize: 12 }}>{v}</Text> },
        { title: t('Status'), dataIndex: 'status', render: (v) => <Tag color={v === 'ready' ? 'green' : 'default'}>{v}</Tag> },
        { title: t('Snapshot'), dataIndex: 'snapshots' },
        { title: t('Size'), render: (_, r) => <Tooltip title={`${t('Raw')} ${bytes(r.raw_bytes)}`}>{bytes(r.size_bytes)}</Tooltip> },
        { title: t('Master key'), render: (_, r) => <Tag color={r.master_current ? 'green' : 'orange'}>{r.master_kid || '-'}</Tag> },
        { title: t('Rotated at'), dataIndex: 'rotated_at', render: (v) => v || '-' },
        {
            title: '', key: 'actions', render: (_, r) => (
                <Popconfirm title={t('Rotate this repo\'s key?')} onConfirm={() => rotate(r)}>
                    <Button size="small" icon={<KeyOutlined />}>{t('Rotate key')}</Button>
                </Popconfirm>
            ),
        },
    ];
    return (
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
            <Space wrap>
                <LockOutlined />
                <Text>{t('Master key')}</Text>
                {mk.configured ? <Tag color="green">{mk.kid}</Tag> : <Tag color="red">{t('Master key not configured')}</Tag>}
                {(mk.kids || []).filter((k) => k !== mk.kid).map((k) => <Tag key={k}>{k}</Tag>)}
                <Button size="small" icon={<ReloadOutlined />} onClick={() => load()} loading={loading} />
            </Space>
            <Alert type="warning" showIcon message={<>{t('If the master key or database is lost, no backup can be restored. Export the keys offline:')} <Text code>php scripts/evm_backup_keys.php export</Text></>} />
            <Table size="small" rowKey="id" loading={loading && !d} dataSource={d?.items || []} columns={columns} pagination={{ pageSize: 50, showSizeChanger: false }} scroll={{ x: 'max-content' }} />
        </Space>
    );
};

// ---------------------------------------------------------------- settings

const SettingsTab = () => {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const [d, load, loading] = useLoad('/settings');
    const [pol] = useLoad('/policies', null, (x) => x?.items || []);
    const [saving, setSaving] = useState(false);
    useEffect(() => { if (d?.settings) form.setFieldsValue(d.settings); }, [d, form]);
    const save = async () => {
        const v = await form.validateFields();
        setSaving(true);
        const r = await call('post', '/settings', { ...d?.settings, ...v });
        setSaving(false);
        if (okRes(r)) { message.success(t('Saved')); load(true); } else message.error(r.msg || t('Failed to save'));
    };
    const n = (name, label, props = {}, tip) => <Form.Item name={name} label={t(label)} tooltip={tip}><InputNumber style={{ width: props.addonAfter ? 150 : 110 }} {...props} /></Form.Item>;
    return (
        <Form form={form} layout="vertical" disabled={loading && !d} style={{ maxWidth: 720 }}>
            <Card size="small" title={t('Schedule')} style={{ marginBottom: 12 }}>
                <Space wrap>
                    <Form.Item name="backup_scheduler_enabled" label={t('Scheduled backups')} valuePropName="checked"><Switch /></Form.Item>
                    {n('backup_window_start', '窗口开始', { min: 0, max: 23, addonAfter: t('h') }, t('Server time'))}
                    {n('backup_window_end', '窗口结束', { min: 0, max: 23, addonAfter: t('h') })}
                </Space>
                <Space wrap>
                    {n('backup_max_jobs', '每节点并发', { min: 1, max: 8 })}
                    {n('backup_upload_mbps', '每节点上传', { min: 0, max: 10000, addonAfter: 'Mbps' }, t('0 means unlimited'))}
                    {n('backup_read_mbps', '每任务读盘', { min: 0, max: 10000, addonAfter: 'MB/s' }, t('0 means unlimited'))}
                </Space>
                <Collapse ghost size="small" items={[{
                    key: 'adv', label: t('Advanced'), children: (
                        <Space wrap>
                            {n('backup_job_timeout_hours', '任务超时', { min: 1, max: 72, addonAfter: t('hours') })}
                            {n('backup_retry_minutes', '失败重试间隔', { min: 5, max: 1440, addonAfter: t('min') })}
                        </Space>
                    ),
                }]} />
            </Card>
            <Card size="small" title={t('Alerts & checks')} style={{ marginBottom: 12 }}>
                <Space wrap>
                    {n('backup_stale_hours', '超时未备份告警', { min: 2, max: 720, addonAfter: t('hours') })}
                    {n('backup_restore_test_days', '恢复演练周期', { min: 0, max: 90, addonAfter: t('Days') }, t('0 = off'))}
                </Space>
                <Collapse ghost size="small" items={[{
                    key: 'adv', label: t('Advanced'), children: (
                        <Space wrap>
                            {n('backup_restore_test_batch', '每次演练数', { min: 1, max: 50 })}
                            {n('backup_check_days', '仓库校验周期', { min: 0, max: 365, addonAfter: t('Days') }, t('0 = off'))}
                            {n('backup_deleted_retention_days', '删除后保留', { min: 0, max: 365, addonAfter: t('Days') }, t('Days to keep backups after the instance is deleted'))}
                        </Space>
                    ),
                }]} />
            </Card>
            <Card size="small" title={t('Add-on')} style={{ marginBottom: 12 }}>
                <Space wrap align="start">
                    <Form.Item name="backup_addon_enabled" label={t('Users can buy')} valuePropName="checked"><Switch /></Form.Item>
                    {n('backup_addon_gb_month', 'Price per GB', { min: 0, step: 0.001, addonAfter: PLATFORM_CURRENCY }, t('Per GB of VM disk, per month; only when the plan has no backups'))}
                    {n('backup_addon_gb_point_month', 'Price per point per GB', { min: 0, step: 0.0001, addonAfter: PLATFORM_CURRENCY }, t('Per restore point, per GB of VM disk, per month'))}
                    {n('backup_addon_min_month', 'Minimum per VM', { min: 0, step: 0.01, addonAfter: PLATFORM_CURRENCY })}
                    <Form.Item name="backup_addon_packs" label={t('Packs')} tooltip={t('Restore points, e.g. 7,14,30')}><Input style={{ width: 140 }} /></Form.Item>
                    {n('backup_addon_interval_hours', 'Interval', { min: 1, max: 744, addonAfter: t('hours') }, t('When the plan has no backups'))}
                    <Form.Item name="backup_addon_policy_id" label={t('Base policy')} tooltip={t('Window, target, manual backups and restore')}>
                        <Select style={{ width: 200 }} options={[{ value: 0, label: t('Automatic') }, ...(pol || []).map((p) => ({ value: p.id, label: p.name }))]} />
                    </Form.Item>
                </Space>
            </Card>
            <Button type="primary" onClick={save} loading={saving}>{t('Save')}</Button>
        </Form>
    );
};

// ---------------------------------------------------------------- page

const BackupManagement = () => {
    const { t } = useTranslation();
    const [tab, setTab] = useState('overview');
    const [nodes, loadNodes, nodesLoading] = useLoad('/nodes', null, (d) => (Array.isArray(d) ? d : []));
    const [areas, setAreas] = useState([]);
    const [zones, setZones] = useState([]);
    useEffect(() => {
        (async () => {
            try {
                const [a, z] = await Promise.all([
                    apiClient.get('/api/evm/admin/area', { params: { per_page: 500 } }).catch(() => null),
                    apiClient.get('/api/evm/admin/zones').catch(() => null),
                ]);
                const zl = z?.data?.data?.zones || [];
                setZones(zl.map((x) => ({ id: Number(x.id), name: x.name || x.code, area_id: x.area_id })));
                const al = a?.data?.data?.data || a?.data?.data || [];
                const m = new Map((Array.isArray(al) ? al : []).map((x) => [Number(x.id), x.name]));
                zl.forEach((x) => { if (x.area_id && !m.has(Number(x.area_id))) m.set(Number(x.area_id), x.area_name || `#${x.area_id}`); });
                setAreas([...m].map(([id, name]) => ({ id, name })));
            } catch (e) { /* optional lists */ }
        })();
    }, []);
    const nodeList = nodes || [];
    const items = [
        { key: 'overview', label: t('Overview'), children: <OverviewTab /> },
        { key: 'targets', label: t('Targets'), children: <TargetsTab nodes={nodeList} areas={areas} zones={zones} /> },
        { key: 'policies', label: t('Policy'), children: <PoliciesTab /> },
        { key: 'jobs', label: t('Task'), children: <JobsTab nodes={nodeList} /> },
        { key: 'points', label: t('Backup points'), children: <PointsTab nodes={nodeList} /> },
        { key: 'vms', label: t('Instances'), children: <VmsTab nodes={nodeList} /> },
        { key: 'nodes', label: t('Node'), children: <NodesTab nodes={nodeList} reload={() => loadNodes()} loading={nodesLoading} /> },
        { key: 'repos', label: t('Key'), children: <ReposTab /> },
        { key: 'settings', label: t('Settings'), children: <SettingsTab /> },
    ];
    return (
        <div>
            <PageHeader title={<Space size={6}>{t('Backup management')}<Tooltip title={t('Encrypted and stored off-site')}><LockOutlined style={{ fontSize: 14 }} /></Tooltip></Space>} />
            <Tabs activeKey={tab} onChange={setTab} destroyOnHidden items={items} />
        </div>
    );
};

export default BackupManagement;
